import React, { useState, useEffect, useCallback } from 'react';
import { X, Mail, Send, Loader2, CheckCircle, AlertCircle, Info } from 'lucide-react';
import { sendInvoiceEmail } from '../services/api';

function Field({ label, required, children, hint }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
      {hint && <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">{hint}</p>}
    </div>
  );
}

const inputCls = `
  w-full px-3.5 py-2.5 text-sm rounded-xl border
  bg-white dark:bg-gray-800
  text-gray-900 dark:text-white
  border-gray-200 dark:border-gray-700
  placeholder-gray-400 dark:placeholder-gray-500
  focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500
  transition-colors
`.replace(/\s+/g, ' ').trim();

function timeAgoShort(isoString) {
  const diff = Date.now() - new Date(isoString).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 2) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return d === 1 ? 'yesterday' : `${d} days ago`;
}

export default function EmailInvoiceModal({ formData, invoiceType, onClose, lastEmailedAt, emailCount }) {
  const business = formData?.business_name || '';
  const clientEmail = formData?.client_email || '';
  const invoiceNumber = formData?.invoice_number || 'DRAFT';

  const defaultMessage = `Dear ${formData?.client_name || 'Sir/Madam'},

Please find attached invoice ${invoiceNumber} for your review and payment.

${formData?.due_date ? `Payment is due by ${formData.due_date}.` : 'Please process payment at your earliest convenience.'}

If you have any questions regarding this invoice, feel free to reach out.

Warm regards,
${business}`;

  const [to, setTo] = useState(clientEmail);
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState(`Invoice ${invoiceNumber} from ${business}`);
  const [message, setMessage] = useState(defaultMessage);

  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleSend = useCallback(async () => {
    if (!to.trim()) return;
    setStatus('sending');
    setErrorMsg('');
    try {
      await sendInvoiceEmail({
        invoice_type: invoiceType,
        data: formData,
        to_email: to.trim(),
        cc_email: cc.trim() || null,
        subject: subject.trim(),
        message: message.trim(),
      });
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setErrorMsg(err?.message || 'Failed to send. Please try again.');
    }
  }, [to, cc, subject, message, invoiceType, formData]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />

      {/* Panel */}
      <div className="relative w-full max-w-lg bg-white dark:bg-gray-900 rounded-2xl shadow-2xl flex flex-col max-h-[90vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-800 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-blue-50 dark:bg-blue-900/40 flex items-center justify-center">
              <Mail className="w-4.5 h-4.5 text-blue-600 dark:text-blue-400" size={18} />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900 dark:text-white">Email Invoice</h2>
              <p className="text-xs text-gray-400 dark:text-gray-500">PDF will be attached automatically</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Success state */}
        {status === 'success' ? (
          <div className="flex flex-col items-center justify-center py-16 px-8 text-center gap-4">
            <div className="w-16 h-16 rounded-full bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-emerald-500" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Invoice sent!</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Invoice <span className="font-medium text-gray-700 dark:text-gray-300">{invoiceNumber}</span> was delivered to{' '}
                <span className="font-medium text-gray-700 dark:text-gray-300">{to}</span>.
              </p>
            </div>
            <button
              onClick={onClose}
              className="mt-2 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-xl transition-colors"
            >
              Done
            </button>
          </div>
        ) : (
          <>
            {/* Form */}
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">

              <Field label="To" required>
                <input
                  type="email"
                  value={to}
                  onChange={e => setTo(e.target.value)}
                  placeholder="client@example.com"
                  className={inputCls}
                  disabled={status === 'sending'}
                />
              </Field>

              <Field label="CC" hint="Optional. Separate multiple addresses with commas.">
                <input
                  type="text"
                  value={cc}
                  onChange={e => setCc(e.target.value)}
                  placeholder="accounts@example.com"
                  className={inputCls}
                  disabled={status === 'sending'}
                />
              </Field>

              <Field label="Subject" required>
                <input
                  type="text"
                  value={subject}
                  onChange={e => setSubject(e.target.value)}
                  className={inputCls}
                  disabled={status === 'sending'}
                />
              </Field>

              <Field label="Message">
                <textarea
                  rows={9}
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  className={`${inputCls} resize-none leading-relaxed`}
                  disabled={status === 'sending'}
                />
              </Field>

              {/* Last sent warning */}
              {lastEmailedAt && (
                <div className="flex items-start gap-2.5 px-3.5 py-3 bg-amber-50 dark:bg-amber-900/20 rounded-xl">
                  <AlertCircle size={15} className="text-amber-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-700 dark:text-amber-300 leading-relaxed">
                    This invoice was last emailed <span className="font-medium">{timeAgoShort(lastEmailedAt)}</span>
                    {emailCount > 1 && ` (${emailCount} times total)`}. Are you sure you want to send again?
                  </p>
                </div>
              )}

              {/* Attachment note */}
              <div className="flex items-start gap-2.5 px-3.5 py-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
                <Info size={15} className="text-blue-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-blue-700 dark:text-blue-300 leading-relaxed">
                  The invoice PDF (<span className="font-medium">{invoiceNumber}.pdf</span>) will be
                  generated and attached automatically when you hit Send.
                </p>
              </div>

              {/* Error */}
              {status === 'error' && (
                <div className="flex items-start gap-2.5 px-3.5 py-3 bg-red-50 dark:bg-red-900/20 rounded-xl">
                  <AlertCircle size={15} className="text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-red-700 dark:text-red-300">{errorMsg}</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-800 flex items-center justify-end gap-3 flex-shrink-0">
              <button
                onClick={onClose}
                disabled={status === 'sending'}
                className="px-4 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSend}
                disabled={!to.trim() || status === 'sending'}
                className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white text-sm font-semibold rounded-xl transition-colors shadow-sm shadow-blue-600/20"
              >
                {status === 'sending' ? (
                  <><Loader2 size={15} className="animate-spin" /> Sending…</>
                ) : (
                  <><Send size={15} /> Send Invoice</>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
